import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Lock,
  ArrowLeft,
  Key,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Shield,
  Info,
} from "lucide-react";
import { toast } from "react-toastify";
import swal from "sweetalert";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import FormControl from "../../components/common/FormControl";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import methodModel from "../../methods/methods";

export default function ChangePassword() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const reduxUser = useSelector((state) => state.user);

  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const isTenantAdmin =
    reduxUser?.isTenantAdmin ||
    reduxUser?.roleType === "tenant_admin" ||
    Boolean(reduxUser?.company_name || reduxUser?.subdomain);

  const handleChange = (field, val) => {
    setForm((prev) => ({ ...prev, [field]: val }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Dynamic password validation checks
  const hasMinLength = form.newPassword.length >= 8 && form.newPassword.length <= 32;
  const hasUpper = /[A-Z]/.test(form.newPassword);
  const hasLower = /[a-z]/.test(form.newPassword);
  const hasNumber = /[0-9]/.test(form.newPassword);
  const hasSpecial = /[@$!%*?&#]/.test(form.newPassword);
  const passwordsMatch =
    Boolean(form.confirmPassword) && form.newPassword === form.confirmPassword;

  const passedCount = [hasMinLength, hasUpper, hasLower, hasNumber, hasSpecial].filter(
    Boolean
  ).length;

  const getStrength = () => {
    if (!form.newPassword) {
      return { label: "Not Entered", color: "bg-slate-200", textColor: "text-slate-500", percent: 0 };
    }
    if (passedCount <= 2) {
      return { label: "Weak", color: "bg-rose-500", textColor: "text-rose-600", percent: 35 };
    }
    if (passedCount <= 4) {
      return { label: "Fair", color: "bg-amber-500", textColor: "text-amber-600", percent: 70 };
    }
    return { label: "Strong & Secure", color: "bg-emerald-500", textColor: "text-emerald-700", percent: 100 };
  };

  const strength = getStrength();

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {};
    if (!form.currentPassword) {
      newErrors.currentPassword = "Current password is required.";
    }

    if (!form.newPassword) {
      newErrors.newPassword = "New password is required.";
    } else {
      const check = methodModel.validateLoginPassword(form.newPassword);
      if (!check.success) {
        newErrors.newPassword = check.message;
      }
    }

    if (!form.confirmPassword) {
      newErrors.confirmPassword = "Confirm password is required.";
    } else if (form.newPassword !== form.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    loader(true);

    const payload = {
      oldPassword: form.currentPassword,
      currentPassword: form.currentPassword,
      newPassword: form.newPassword,
      confirmPassword: form.confirmPassword,
    };

    // Primary endpoint for tenant admin / admin: user/admin/change-password
    // Fallback endpoint for customer: user/change/password
    const primaryUrl = isTenantAdmin ? "user/admin/change-password" : "user/change/password";

    try {
      let res;
      try {
        res = await ApiClient.put(primaryUrl, payload);
      } catch (errPrimary) {
        // Fallback to alternate route
        const fallbackUrl = isTenantAdmin ? "user/change/password" : "user/admin/change-password";
        res = await ApiClient.put(fallbackUrl, payload);
      }

      loader(false);
      setLoading(false);

      if (res?.success || res?.status === 200 || res?.message?.toLowerCase().includes("success")) {
        swal({
          icon: "success",
          title: "Password Updated!",
          text: "Your password has been changed successfully.",
          confirmButtonColor: "#115B9D",
        }).then(() => {
          navigate("/profile");
        });
      } else {
        toast.error(res?.message || "Failed to change password. Please check your current password.");
      }
    } catch (err) {
      loader(false);
      setLoading(false);
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error?.message ||
        "Failed to change password. Please verify your current password.";
      toast.error(errMsg);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-[calc(100vh_-_75px)] md:min-h-[calc(100vh_-_95px)] mt-[75px] lg:mt-[95px] bg-[#F4F7FB] py-8 lg:py-12">
        <div className="container mx-auto px-3 sm:px-6 max-w-4xl space-y-6">
          {/* Top Actions Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-300 shadow-sm">
            <div className="flex items-center gap-3">
              <Link
                to="/profile"
                className="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 transition-colors cursor-pointer shrink-0"
                title="Back to Profile"
              >
                <ArrowLeft className="w-4 h-4" />
              </Link>
              <div>
                <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                  Change Password
                </h1>
                <p className="text-xs sm:text-sm text-slate-700 font-medium mt-0.5">
                  Update your authentication credentials and keep your account secure.
                </p>
              </div>
            </div>

            <div className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-100 text-[#115B9D] border border-blue-300 text-xs font-bold shrink-0">
              <ShieldCheck className="w-4 h-4" />
              <span>Security Center</span>
            </div>
          </div>

          {/* Balanced 2-Column Grid: Form + Live Security Rules */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column: Password Inputs Form */}
            <div className="lg:col-span-7 bg-white rounded-2xl shadow-sm border border-slate-300 p-5 sm:p-7 space-y-5">
              <div className="flex items-center gap-2 border-b border-slate-200 pb-3">
                <Key className="w-4 h-4 text-[#115B9D]" />
                <h2 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                  Account Credentials
                </h2>
              </div>

              <form onSubmit={handleSubmit} noValidate className="space-y-4">
                <FormControl
                  type="password"
                  name="currentPassword"
                  label="Current Password"
                  required={true}
                  showEye={true}
                  placeholder="Enter current password"
                  value={form.currentPassword}
                  error={errors.currentPassword}
                  onChange={(val) => handleChange("currentPassword", val)}
                />

                <FormControl
                  type="password"
                  name="newPassword"
                  label="New Password"
                  required={true}
                  showEye={true}
                  placeholder="Enter new password (min. 8 characters)"
                  value={form.newPassword}
                  error={errors.newPassword}
                  onChange={(val) => handleChange("newPassword", val)}
                />

                <FormControl
                  type="password"
                  name="confirmPassword"
                  label="Confirm New Password"
                  required={true}
                  showEye={true}
                  placeholder="Re-enter new password"
                  value={form.confirmPassword}
                  error={errors.confirmPassword}
                  onChange={(val) => handleChange("confirmPassword", val)}
                />

                {/* Password Match Confirmation Status */}
                {form.confirmPassword && (
                  <div
                    className={`flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-xl border ${
                      passwordsMatch
                        ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                        : "bg-rose-50 text-rose-800 border-rose-300"
                    }`}
                  >
                    {passwordsMatch ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span>Passwords match successfully</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 text-rose-600 shrink-0" />
                        <span>Passwords do not match yet</span>
                      </>
                    )}
                  </div>
                )}

                {/* Form Action Buttons */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
                  <Link
                    to="/profile"
                    className="px-5 h-11 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 text-xs font-bold transition-all flex items-center justify-center shadow-2xs cursor-pointer"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 px-6 h-11 rounded-xl bg-gradient-to-r from-[#115B9D] via-[#0E528F] to-[#0A3E6E] hover:from-[#0E528F] hover:to-[#08335C] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer disabled:opacity-75"
                  >
                    <span>{loading ? "Updating Password..." : "Update Password"}</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </div>

            {/* Right Column: Interactive Password Strength & Rules Checklist */}
            <div className="lg:col-span-5 space-y-5">
              {/* Strength Card */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-300 p-5 sm:p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Password Strength
                  </span>
                  <span className={`text-xs font-black ${strength.textColor}`}>
                    {strength.label}
                  </span>
                </div>

                {/* Visual Progress Bar */}
                <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${strength.color}`}
                    style={{ width: `${strength.percent}%` }}
                  />
                </div>

                {/* Live Checklist */}
                <div className="pt-2 border-t border-slate-200 space-y-2.5">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block">
                    Security Requirements
                  </span>

                  <ul className="space-y-2 text-xs">
                    <li className="flex items-center gap-2">
                      {hasMinLength ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" />
                      )}
                      <span
                        className={
                          hasMinLength
                            ? "font-bold text-slate-900"
                            : "text-slate-600 font-medium"
                        }
                      >
                        At least 8 characters (max 32)
                      </span>
                    </li>

                    <li className="flex items-center gap-2">
                      {hasUpper ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" />
                      )}
                      <span
                        className={
                          hasUpper
                            ? "font-bold text-slate-900"
                            : "text-slate-600 font-medium"
                        }
                      >
                        One uppercase letter (A-Z)
                      </span>
                    </li>

                    <li className="flex items-center gap-2">
                      {hasLower ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" />
                      )}
                      <span
                        className={
                          hasLower
                            ? "font-bold text-slate-900"
                            : "text-slate-600 font-medium"
                        }
                      >
                        One lowercase letter (a-z)
                      </span>
                    </li>

                    <li className="flex items-center gap-2">
                      {hasNumber ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" />
                      )}
                      <span
                        className={
                          hasNumber
                            ? "font-bold text-slate-900"
                            : "text-slate-600 font-medium"
                        }
                      >
                        At least one number (0-9)
                      </span>
                    </li>

                    <li className="flex items-center gap-2">
                      {hasSpecial ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" />
                      )}
                      <span
                        className={
                          hasSpecial
                            ? "font-bold text-slate-900"
                            : "text-slate-600 font-medium"
                        }
                      >
                        One special symbol (@$!%*?&amp;#)
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Best Practice Tips Box */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-300 p-4 sm:p-5 text-xs text-slate-700 space-y-2">
                <div className="flex items-center gap-2 font-bold text-slate-900">
                  <Shield className="w-4 h-4 text-[#115B9D]" />
                  <span>Account Safety Best Practices</span>
                </div>
                <p className="text-[11px] text-slate-600 leading-relaxed font-medium">
                  Use a unique combination of letters, digits, and symbols that you do not reuse across other business portals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {methodModel.TokenChecker() && <Footer />}
    </>
  );
}
